import { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { setToken } from "../lib/auth";
import { Lock, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PIN_LENGTH = 6;

export default function Login() {
  const navigate = useNavigate();
  const [digits, setDigits] = useState(Array(PIN_LENGTH).fill(""));
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const inputRefs = useRef([]);

  useEffect(() => {
    inputRefs.current[0]?.focus();
  }, []);

  const submitPin = async (pin) => {
    setError("");
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/auth/login`, { password: pin });
      setToken(data.token);
      navigate("/dashboard", { replace: true });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Login failed. Please try again.");
      setDigits(Array(PIN_LENGTH).fill(""));
      inputRefs.current[0]?.focus();
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (index, value) => {
    const clean = value.replace(/\D/g, "");
    if (!clean) {
      const next = [...digits];
      next[index] = "";
      setDigits(next);
      return;
    }
    const next = [...digits];
    const chars = clean.split("");
    let i = index;
    for (const ch of chars) {
      if (i >= PIN_LENGTH) break;
      next[i] = ch;
      i += 1;
    }
    setDigits(next);
    const nextEmpty = next.findIndex((d) => d === "");
    if (nextEmpty === -1) {
      inputRefs.current[PIN_LENGTH - 1]?.blur();
      submitPin(next.join(""));
    } else {
      inputRefs.current[Math.min(i, PIN_LENGTH - 1)]?.focus();
    }
  };

  const handleKeyDown = (index, e) => {
    if (e.key === "Backspace" && !digits[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, PIN_LENGTH);
    if (!pasted) return;
    const next = Array(PIN_LENGTH).fill("");
    pasted.split("").forEach((ch, i) => (next[i] = ch));
    setDigits(next);
    if (pasted.length === PIN_LENGTH) {
      submitPin(pasted);
    } else {
      inputRefs.current[pasted.length]?.focus();
    }
  };

  return (
    <div
      className="min-h-screen w-full flex items-center justify-center px-6 py-12 relative overflow-hidden"
      style={{
        background:
          "radial-gradient(ellipse at top, #1e3a5f 0%, #0f172a 50%, #020617 100%)",
      }}
      data-testid="login-page"
    >
      {/* Grain overlay */}
      <div
        aria-hidden
        className="absolute inset-0 opacity-[0.04] pointer-events-none"
        style={{
          backgroundImage:
            "url(\"data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' /%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)' /%3E%3C/svg%3E\")",
        }}
      />

      {/* Ambient glow */}
      <div className="absolute -top-40 -right-40 w-96 h-96 rounded-full bg-amber-500/10 blur-3xl" />
      <div className="absolute -bottom-40 -left-40 w-96 h-96 rounded-full bg-blue-500/10 blur-3xl" />

      <div className="w-full max-w-md relative z-10">
        {/* Logo / Brand */}
        <div className="text-center mb-10">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-gradient-to-br from-amber-400 to-amber-600 shadow-lg shadow-amber-500/20 mb-5">
            <span className="text-3xl">👑</span>
          </div>
          <h1
            className="text-4xl font-bold tracking-tight text-white mb-2"
            style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
          >
            Videra Wealth
          </h1>
          <p className="text-sm text-slate-400 tracking-wide uppercase">
            AI Investment Advisor
          </p>
        </div>

        {/* Card */}
        <div
          className="rounded-2xl p-8 shadow-2xl"
          style={{
            background: "rgba(15, 23, 42, 0.6)",
            backdropFilter: "blur(20px)",
            border: "1px solid rgba(148, 163, 184, 0.1)",
          }}
        >
          <div className="flex items-center gap-2 mb-6 justify-center">
            <Lock className="w-4 h-4 text-amber-500" />
            <h2 className="text-base font-medium text-slate-200">Enter your access PIN</h2>
          </div>

          <div className="space-y-5">
            <div
              className="flex items-center justify-center gap-2 sm:gap-3"
              data-testid="login-pin-inputs"
              onPaste={handlePaste}
            >
              {digits.map((digit, index) => (
                <input
                  key={index}
                  ref={(el) => (inputRefs.current[index] = el)}
                  type="password"
                  inputMode="numeric"
                  maxLength={1}
                  value={digit}
                  disabled={loading}
                  onChange={(e) => handleChange(index, e.target.value)}
                  onKeyDown={(e) => handleKeyDown(index, e)}
                  data-testid={`login-pin-digit-${index}`}
                  className="w-11 h-13 sm:w-12 sm:h-14 text-center text-xl font-semibold rounded-lg bg-slate-900/60 border border-slate-700/70 text-white focus:outline-none focus:border-amber-500/60 focus:ring-1 focus:ring-amber-500/40 transition-colors disabled:opacity-40"
                />
              ))}
            </div>

            {error && (
              <div
                className="text-sm text-red-400 bg-red-500/10 border border-red-500/20 rounded-lg px-3 py-2 text-center"
                data-testid="login-error"
              >
                {error}
              </div>
            )}

            {loading && (
              <div className="flex items-center justify-center gap-2 text-sm text-slate-400" data-testid="login-verifying">
                <Loader2 className="w-4 h-4 animate-spin" />
                Verifying...
              </div>
            )}
          </div>

          <p className="text-center text-xs text-slate-500 mt-6">
            Private access • Session valid for 30 days
          </p>
        </div>

        <p className="text-center text-xs text-slate-600 mt-8 tracking-wider">
          VIDERA · SEE THE MARKET DIFFERENTLY
        </p>
      </div>
    </div>
  );
}
